import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import api, { articlesAPI, categoriesAPI, mediaAPI, notesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Save, ArrowLeft, Image, Upload, Video, Plus, X, Link, Eye, MessageSquare, Send, Trash2 } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { getImageUrl, formatDate } from '../../lib/utils';

// Client-side image compression using Canvas API
const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const img = new window.Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxWidth) { resolve(file); return; }
      height = Math.round((height * maxWidth) / width);
      width = maxWidth;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() })),
        'image/jpeg',
        quality
      );
    };
    img.src = URL.createObjectURL(file);
  });
};

const TipTapEditor = ({ content, onChange, onAddImage }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      ImageExtension.configure({
        HTMLAttributes: {
          class: 'article-image',
        },
      }),
      LinkExtension.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Start writing your article...',
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      transformPastedHTML(html) {
        // Remove Word/Google Docs junk styles
        let clean = html
          .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
          .replace(/\sclass="[^"]*"/gi, '')
          .replace(/\sstyle="[^"]*"/gi, '')
          .replace(/\s(data-\w+)="[^"]*"/gi, '')
          // Collapse multiple <br> into one paragraph break
          .replace(/(<br\s*\/?\s*>[\s\n]*){2,}/gi, '</p><p>')
          // Remove empty paragraphs
          .replace(/<p>\s*(<br\s*\/?\s*>)?\s*<\/p>/gi, '')
          // Remove non-breaking spaces used as spacers
          .replace(/(&nbsp;\s*){2,}/gi, ' ')
          // Remove spans that just wrap text
          .replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, '$1');
        return clean;
      },
    },
  });

  useEffect(() => {
    if (editor && content && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const addImageToEditor = (url) => {
    if (editor && url) {
      editor.chain().focus().setImage({ src: url, alt: 'Article image' }).run();
      // Add paragraph after image so cursor can continue
      const { state } = editor.view;
      const pos = state.selection.to;
      editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
    }
  };

  // Expose addImage function - use callback ref to avoid stale closure
  useEffect(() => {
    if (onAddImage && editor) {
      onAddImage((url) => {
        if (editor && url) {
          editor.chain().focus().setImage({ src: url, alt: 'Article image' }).run();
          const { state } = editor.view;
          const pos = state.selection.to;
          editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
        }
      });
    }
  }, [editor, onAddImage]);

  if (!editor) return null;

  return (
    <div className="border border-border rounded-sm">
      {/* Toolbar - Scrollable on mobile */}
      <div className="overflow-x-auto">
        <div className="flex gap-1 p-2 border-b border-border bg-secondary/30 min-w-max">
          <Button
            type="button"
            variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
            size="sm"
            className="min-w-[36px] h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </Button>
          <Button
            type="button"
            variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
            size="sm"
            className="min-w-[36px] h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <em>I</em>
          </Button>
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 2 }) ? 'secondary' : 'ghost'}
            size="sm"
            className="min-w-[36px] h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </Button>
          <Button
            type="button"
            variant={editor.isActive('heading', { level: 3 }) ? 'secondary' : 'ghost'}
            size="sm"
            className="min-w-[36px] h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </Button>
          <Button
            type="button"
            variant={editor.isActive('bulletList') ? 'secondary' : 'ghost'}
            size="sm"
            className="h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            • List
          </Button>
          <Button
            type="button"
            variant={editor.isActive('orderedList') ? 'secondary' : 'ghost'}
            size="sm"
            className="h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            1. List
          </Button>
          <Button
            type="button"
            variant={editor.isActive('blockquote') ? 'secondary' : 'ghost'}
            size="sm"
            className="h-9 touch-manipulation"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            Quote
          </Button>
          <div className="w-px h-6 bg-border mx-1 self-center" />
          
          {/* Multiple Images Upload */}
          <label className="cursor-pointer touch-manipulation">
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={async (e) => {
                const files = e.target.files;
                if (!files?.length) return;
                try {
                  const { mediaAPI } = await import('../../lib/api');
                  const { toast } = await import('sonner');
                  const toastId = toast.loading(`Uploading ${files.length} image(s)...`);
                  const compressed = await Promise.all(
                    Array.from(files).map(f => compressImage(f))
                  );
                  const results = await Promise.all(
                    compressed.map(f => {
                      const fd = new FormData();
                      fd.append('file', f);
                      return mediaAPI.upload(fd);
                    })
                  );
                  let inserted = 0;
                  for (const res of results) {
                    if (res.data?.url) {
                      const fullUrl = res.data.url.startsWith('http') 
                        ? res.data.url 
                        : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                      editor.chain().focus().setImage({ src: fullUrl, alt: 'Article image' }).run();
                      // Force a paragraph after the image so cursor has somewhere to go
                      const { state } = editor.view;
                      const pos = state.selection.to;
                      editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
                      inserted++;
                    }
                  }
                  toast.dismiss(toastId);
                  toast.success(`${inserted} image(s) inserted into article`);
                } catch (error) {
                  console.error('Failed to upload images:', error);
                  const { toast } = await import('sonner');
                  toast.error('Failed to upload images');
                }
                e.target.value = '';
              }}
              className="hidden"
            />
            <Button type="button" variant="ghost" size="sm" className="h-9 touch-manipulation" asChild>
              <span className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
                  <circle cx="9" cy="9" r="2"/>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                </svg>
                <span className="hidden sm:inline">Images</span>
                <span className="sm:hidden">📷</span>
              </span>
            </Button>
          </label>

          {/* Video Embed Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 touch-manipulation"
            onClick={async () => {
              const url = window.prompt('Enter YouTube, Vimeo, or video URL:');
              if (!url) return;
              
              let embedHtml = '';
              
              if (url.includes('youtube.com') || url.includes('youtu.be')) {
                let videoId = '';
                if (url.includes('youtu.be/')) {
                  videoId = url.split('youtu.be/')[1]?.split('?')[0];
                } else if (url.includes('watch?v=')) {
                  videoId = url.split('watch?v=')[1]?.split('&')[0];
                } else if (url.includes('shorts/')) {
                  videoId = url.split('shorts/')[1]?.split('?')[0];
                } else if (url.includes('/embed/')) {
                  videoId = url.split('/embed/')[1]?.split('?')[0];
                }
                if (videoId) {
                  embedHtml = `<div class="video-embed" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:20px 0;"><iframe src="https://www.youtube.com/embed/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen></iframe></div>`;
                }
              }
              else if (url.includes('vimeo.com')) {
                const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                if (videoId) {
                  embedHtml = `<div class="video-embed" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:20px 0;"><iframe src="https://player.vimeo.com/video/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allowfullscreen></iframe></div>`;
                }
              }
              else if (url.match(/\.(mp4|webm|ogg)$/i) || url.includes('/api/uploads/')) {
                embedHtml = `<div class="video-embed" style="margin:20px 0;"><video src="${url}" controls style="width:100%;max-height:500px;"></video></div>`;
              }
              
              if (embedHtml) {
                editor.chain().focus().insertContent(embedHtml + '<p></p>').run();
                const { toast } = await import('sonner');
                toast.success('Video embedded');
              } else {
                const { toast } = await import('sonner');
                toast.error('Could not parse video URL');
              }
            }}
          >
            <span className="flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7"/>
                <rect width="15" height="14" x="1" y="5" rx="2" ry="2"/>
              </svg>
              <span className="hidden sm:inline">Video</span>
              <span className="sm:hidden">🔗</span>
            </span>
          </Button>

          {/* Upload Video File */}
          <label className="cursor-pointer touch-manipulation">
            <input
              type="file"
              accept="video/*"
              capture="environment"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                
                if (file.size > 100 * 1024 * 1024) {
                  const { toast } = await import('sonner');
                  toast.error('Video must be less than 100MB');
                  return;
                }
                
                try {
                  const { mediaAPI } = await import('../../lib/api');
                  const { toast } = await import('sonner');
                  toast.info('Uploading video...');
                  
                  const formData = new FormData();
                  formData.append('file', file);
                  const res = await mediaAPI.upload(formData);
                  
                  if (res.data?.url) {
                    const fullUrl = res.data.url.startsWith('http') 
                      ? res.data.url 
                      : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                    const embedHtml = `<div class="video-embed" style="margin:20px 0;"><video src="${fullUrl}" controls style="width:100%;max-height:500px;"></video></div>`;
                    editor.chain().focus().insertContent(embedHtml + '<p></p>').run();
                    toast.success('Video uploaded');
                  }
                } catch (error) {
                  console.error('Failed to upload video:', error);
                  const { toast } = await import('sonner');
                  toast.error('Failed to upload video');
                }
                e.target.value = '';
              }}
              className="hidden"
            />
            <Button type="button" variant="ghost" size="sm" className="h-9 touch-manipulation" asChild>
              <span className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" x2="12" y1="3" y2="15"/>
                </svg>
                <span className="hidden sm:inline">Upload Video</span>
                <span className="sm:hidden">🎬</span>
              </span>
            </Button>
          </label>
        </div>
      </div>
      <EditorContent editor={editor} className="tiptap-editor min-h-[300px] p-3" />
    </div>
  );
};

export const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [addImageToEditor, setAddImageToEditor] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    subheadline: '',
    category_id: '',
    tags: [],
    author: '',
    status: 'draft',
    publish_date: '',
    hero_type: 'image',
    hero_image: '',
    hero_video: '',
    hero_video_file: '',
    hero_video_type: 'url', // 'url' or 'upload'
    body: '',
    photo_credits: '',
    meta_title: '',
    meta_description: '',
    og_image: '',
    is_featured: false,
    is_breaking: false,
    is_headline_featured: false,
    // New fields for embedded media
    gallery_images: [],
    embedded_video: '',
    embedded_video_file: '',
    embedded_video_type: 'url', // 'url' or 'upload'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const catsRes = await categoriesAPI.getAll();
        setCategories(catsRes.data);

        if (isEditing) {
          const articleRes = await articlesAPI.getById(id);
          const article = articleRes.data;
          setFormData({
            title: article.title || '',
            subheadline: article.subheadline || '',
            category_id: article.category_id || '',
            tags: article.tags || [],
            author: article.author || '',
            status: article.status || 'draft',
            publish_date: article.publish_date 
              ? article.publish_date.slice(0, 16) // Format for datetime-local: YYYY-MM-DDTHH:mm
              : '',
            hero_type: article.hero_type || 'image',
            hero_image: article.hero_image || '',
            hero_video: article.hero_video || '',
            hero_video_file: article.hero_video_file || '',
            hero_video_type: article.hero_video_type || 'url',
            body: article.body || '',
            photo_credits: article.photo_credits || '',
            meta_title: article.meta_title || '',
            meta_description: article.meta_description || '',
            og_image: article.og_image || '',
            is_featured: article.is_featured || false,
            is_breaking: article.is_breaking || false,
            is_headline_featured: article.is_headline_featured || false,
            gallery_images: article.gallery_images || [],
            embedded_video: article.embedded_video || '',
            embedded_video_file: article.embedded_video_file || '',
            embedded_video_type: article.embedded_video_type || 'url',
          });
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing]);

  // Fetch editorial notes when editing
  useEffect(() => {
    if (isEditing && id) {
      fetchNotes();
    }
  }, [id, isEditing]);

  const fetchNotes = async () => {
    if (!id) return;
    setLoadingNotes(true);
    try {
      const res = await notesAPI.getForArticle(id);
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return;
    
    setAddingNote(true);
    try {
      await notesAPI.create(id, newNote.trim());
      toast.success('Note added');
      setNewNote('');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await notesAPI.delete(id, noteId);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e, field = 'hero_image') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const formDataUpload = new FormData();
      formDataUpload.append('file', compressed);
      const res = await mediaAPI.upload(formDataUpload);
      if (res.data?.url) {
        setFormData(prev => ({ ...prev, [field]: res.data.url }));
        toast.success('Image uploaded');
      } else {
        toast.error('Upload failed - no URL returned');
      }
    } catch (error) {
      console.error('Hero image upload error:', error);
      toast.error('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploadingGallery(true);
    try {
      toast.info(`Compressing & uploading ${files.length} image(s)...`);
      const compressed = await Promise.all(
        Array.from(files).map(f => compressImage(f))
      );
      const results = await Promise.all(
        compressed.map(f => {
          const formDataUpload = new FormData();
          formDataUpload.append('file', f);
          return mediaAPI.upload(formDataUpload);
        })
      );
      const uploadedUrls = results
        .filter(r => r.data?.url)
        .map(r => r.data.url);
      if (uploadedUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          gallery_images: [...prev.gallery_images, ...uploadedUrls]
        }));
        toast.success(`${uploadedUrls.length} image(s) uploaded`);
      } else {
        toast.error('No images were uploaded successfully');
      }
    } catch (error) {
      console.error('Gallery upload error:', error);
      toast.error('Failed to upload images: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingGallery(false);
      e.target.value = '';
    }
  };

  const handleVideoUpload = async (e, field = 'hero_video_file') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video file must be less than 100MB');
      return;
    }

    setUploadingVideo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await mediaAPI.upload(formDataUpload);
      
      if (field === 'hero_video_file') {
        setFormData(prev => ({ 
          ...prev, 
          hero_video_file: res.data.url,
          hero_video_type: 'upload'
        }));
      } else if (field === 'embedded_video_file') {
        setFormData(prev => ({ 
          ...prev, 
          embedded_video_file: res.data.url,
          embedded_video_type: 'upload'
        }));
      }
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const insertImageIntoBody = (url) => {
    if (addImageToEditor) {
      const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
      addImageToEditor(fullUrl);
      toast.success('Image inserted into article');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category_id || !formData.author) {
      toast.error('Please fill in required fields (Title, Category, Author)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        publish_date: formData.publish_date ? new Date(formData.publish_date).toISOString() : null,
      };

      if (isEditing) {
        await articlesAPI.update(id, payload);
        toast.success('Article updated');
      } else {
        await articlesAPI.create(payload);
        toast.success('Article created');
      }
      navigate('/staff/articles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="space-y-8" data-testid="article-editor-form">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/staff/articles')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold">
                {isEditing ? 'Edit Article' : 'New Article'}
              </h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPreview(true)}
              data-testid="preview-article-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Select
              value={formData.status}
              onValueChange={(value) => handleSelectChange('status', value)}
            >
              <SelectTrigger className="w-[140px]" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={saving} data-testid="save-article-btn">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="text-lg"
                placeholder="Article title..."
                data-testid="article-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subheadline">Subheadline / Deck</Label>
              <Textarea
                id="subheadline"
                name="subheadline"
                value={formData.subheadline}
                onChange={handleChange}
                rows={2}
                placeholder="Brief description..."
                data-testid="article-subheadline-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Body Content</Label>
              <p className="text-xs text-muted-foreground mb-2">
                💡 Click where you want to add media, then use <strong>Images</strong> (multiple), <strong>Video</strong> (embed URL), or <strong>Upload Video</strong> buttons in the toolbar.
              </p>
              <TipTapEditor
                content={formData.body}
                onChange={(html) => setFormData(prev => ({ ...prev, body: html }))}
                onAddImage={setAddImageToEditor}
              />
            </div>

            {/* Photo Gallery Section */}
            <div className="border border-border rounded-sm p-4 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-medium flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Photo Gallery
                </h3>
                <label className="cursor-pointer touch-manipulation">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    onChange={handleGalleryUpload}
                    className="hidden"
                  />
                  <Button type="button" variant="outline" size="sm" className="h-10 touch-manipulation" disabled={uploadingGallery} asChild>
                    <span>
                      {uploadingGallery ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add Photos
                    </span>
                  </Button>
                </label>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Tap photos to insert them into your article body.
              </p>

              {formData.gallery_images.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {formData.gallery_images.map((url, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={getImageUrl(url)}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover rounded-sm cursor-pointer hover:opacity-80 active:opacity-70 transition-opacity"
                        onClick={() => insertImageIntoBody(url)}
                        title="Tap to insert into article"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.querySelector('.broken-gallery') && (e.target.parentElement.querySelector('.broken-gallery').style.display = 'flex');
                        }}
                      />
                      <div className="broken-gallery w-full h-full rounded-sm bg-destructive/10 border border-destructive/30 items-center justify-center text-destructive text-xs p-2 text-center" style={{display: 'none'}}>
                        Broken image
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeGalleryImage(index); }}
                        className="absolute top-1 right-1 p-1.5 bg-black/70 hover:bg-destructive rounded-sm transition-colors"
                        title="Remove image"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertImageIntoBody(url)}
                        className="absolute bottom-0 left-0 right-0 py-2 bg-black/70 text-white text-xs md:opacity-0 md:group-hover:opacity-100 transition-opacity text-center"
                      >
                        Tap to Insert
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-border rounded-sm p-8 text-center text-muted-foreground">
                  <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No photos uploaded yet</p>
                </div>
              )}
            </div>

            {/* Embedded Video Section */}
            <div className="border border-border rounded-sm p-4 space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Video className="h-5 w-5" />
                Embedded Video
              </h3>
              <p className="text-sm text-muted-foreground">
                Add a video that will appear within your article body.
              </p>
              
              {/* Video Source Toggle */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.embedded_video_type === 'url' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, embedded_video_type: 'url' }))}
                  className="flex-1"
                >
                  <Link className="h-4 w-4 mr-2" />
                  YouTube/Link
                </Button>
                <Button
                  type="button"
                  variant={formData.embedded_video_type === 'upload' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, embedded_video_type: 'upload' }))}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Video
                </Button>
              </div>

              {formData.embedded_video_type === 'url' ? (
                <div className="space-y-2">
                  <Label htmlFor="embedded_video">Video URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="embedded_video"
                      name="embedded_video"
                      value={formData.embedded_video}
                      onChange={handleChange}
                      placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                      className="flex-1"
                      data-testid="embedded-video-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (formData.embedded_video) {
                          const videoHtml = `<div class="video-embed"><iframe src="${formData.embedded_video.replace('watch?v=', 'embed/')}" frameborder="0" allowfullscreen></iframe></div>`;
                          setFormData(prev => ({
                            ...prev,
                            body: prev.body + videoHtml
                          }));
                          toast.success('Video embedded in article');
                        }
                      }}
                    >
                      <Link className="h-4 w-4 mr-2" />
                      Embed
                    </Button>
                  </div>
                  {formData.embedded_video && (
                    <div className="aspect-video bg-muted rounded-sm flex items-center justify-center">
                      <Video className="h-12 w-12 text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Video preview</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Upload Video File</Label>
                  {formData.embedded_video_file ? (
                    <div className="space-y-2">
                      <div className="p-3 bg-secondary/50 rounded-sm flex items-center gap-2">
                        <Video className="h-4 w-4 text-primary" />
                        <span className="text-sm truncate flex-1">{formData.embedded_video_file.split('/').pop()}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, embedded_video_file: '' }))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <video 
                        src={getImageUrl(formData.embedded_video_file)} 
                        className="w-full aspect-video object-cover rounded-sm bg-black"
                        controls
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          const videoUrl = getImageUrl(formData.embedded_video_file);
                          const videoHtml = `<div class="video-embed"><video src="${videoUrl}" controls style="width:100%;max-width:100%;"></video></div>`;
                          setFormData(prev => ({
                            ...prev,
                            body: prev.body + videoHtml
                          }));
                          toast.success('Video embedded in article');
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Insert Video into Article
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block touch-manipulation">
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        capture="environment"
                        onChange={(e) => handleVideoUpload(e, 'embedded_video_file')}
                        className="hidden"
                      />
                      <div className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-primary/50 transition-colors active:border-primary/50">
                        {uploadingVideo ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">Uploading video...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="h-8 w-8 text-muted-foreground" />
                            <p className="text-sm font-medium">Tap to upload video</p>
                            <p className="text-xs text-muted-foreground">MP4, WebM, MOV (max 100MB)</p>
                          </div>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Category & Author */}
            <div className="border border-border rounded-sm p-4 space-y-4">
              <h3 className="font-medium">Details</h3>
              
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => handleSelectChange('category_id', value)}
                  required
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="author">Author *</Label>
                <Input
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  required
                  data-testid="article-author-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="publish_date">
                  Publish Date & Time
                  {formData.status === 'scheduled' && (
                    <span className="text-xs text-muted-foreground ml-2">
                      (Article will auto-publish at this time)
                    </span>
                  )}
                </Label>
                <Input
                  id="publish_date"
                  name="publish_date"
                  type="datetime-local"
                  value={formData.publish_date}
                  onChange={handleChange}
                  data-testid="publish-date-input"
                />
              </div>
            </div>

            {/* Hero Media */}
            <div className="border border-border rounded-sm p-4 space-y-4">
              <h3 className="font-medium">Hero Media (Top of Article)</h3>
              
              <Tabs value={formData.hero_type} onValueChange={(v) => handleSelectChange('hero_type', v)}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="image" data-testid="hero-type-image">
                    <Image className="h-4 w-4 mr-2" />
                    Image
                  </TabsTrigger>
                  <TabsTrigger value="video" data-testid="hero-type-video">
                    <Video className="h-4 w-4 mr-2" />
                    Video
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="image" className="space-y-3 mt-4">
                  {formData.hero_image && (
                    <div className="relative">
                      <img 
                        src={formData.hero_image.startsWith('http') 
                          ? formData.hero_image 
                          : `${process.env.REACT_APP_BACKEND_URL}${formData.hero_image}`}
                        alt="Hero"
                        className="w-full aspect-video object-cover rounded-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.querySelector('.broken-msg') && (e.target.parentElement.querySelector('.broken-msg').style.display = 'flex');
                        }}
                      />
                      <div className="broken-msg w-full aspect-video rounded-sm bg-destructive/10 border border-destructive/30 items-center justify-center text-destructive text-sm" style={{display: 'none'}}>
                        Image not found — please re-upload
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, hero_image: '' }))}
                        className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-destructive rounded-sm transition-colors"
                        title="Remove hero image"
                        data-testid="remove-hero-image"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      name="hero_image"
                      value={formData.hero_image}
                      onChange={handleChange}
                      placeholder="Image URL or upload"
                      className="flex-1"
                      data-testid="hero-image-input"
                    />
                    <label className="cursor-pointer touch-manipulation">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleImageUpload(e, 'hero_image')}
                        className="hidden"
                      />
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10" disabled={uploading} asChild>
                        <span>
                          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        </span>
                      </Button>
                    </label>
                  </div>
                </TabsContent>
                
                <TabsContent value="video" className="space-y-4 mt-4">
                  {/* Video Source Toggle */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.hero_video_type === 'url' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, hero_video_type: 'url' }))}
                      className="flex-1"
                      data-testid="hero-video-url-btn"
                    >
                      <Link className="h-4 w-4 mr-2" />
                      YouTube/Link
                    </Button>
                    <Button
                      type="button"
                      variant={formData.hero_video_type === 'upload' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, hero_video_type: 'upload' }))}
                      className="flex-1"
                      data-testid="hero-video-upload-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Video
                    </Button>
                  </div>

                  {formData.hero_video_type === 'url' ? (
                    <div className="space-y-2">
                      <Label htmlFor="hero_video">Video URL</Label>
                      <Input
                        id="hero_video"
                        name="hero_video"
                        value={formData.hero_video}
                        onChange={handleChange}
                        placeholder="https://youtube.com/watch?v=... or https://instagram.com/reel/..."
                        data-testid="hero-video-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Supports: YouTube, YouTube Shorts, Instagram Reels, Vimeo
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Upload Video File</Label>
                      {formData.hero_video_file ? (
                        <div className="space-y-2">
                          <div className="p-3 bg-secondary/50 rounded-sm flex items-center gap-2">
                            <Video className="h-4 w-4 text-primary" />
                            <span className="text-sm truncate flex-1">{formData.hero_video_file.split('/').pop()}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, hero_video_file: '' }))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <video 
                            src={getImageUrl(formData.hero_video_file)} 
                            className="w-full aspect-video object-cover rounded-sm bg-black"
                            controls
                          />
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <input
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime"
                            onChange={(e) => handleVideoUpload(e, 'hero_video_file')}
                            className="hidden"
                          />
                          <div className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-primary/50 transition-colors">
                            {uploadingVideo ? (
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <p className="text-sm text-muted-foreground">Uploading video...</p>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-2">
                                <Upload className="h-8 w-8 text-muted-foreground" />
                                <p className="text-sm font-medium">Click to upload video</p>
                                <p className="text-xs text-muted-foreground">MP4, WebM, MOV (max 100MB)</p>
                              </div>
                            )}
                          </div>
                        </label>
                      )}
                    </div>
                  )}

                  {/* Preview for URL videos */}
                  {formData.hero_video_type === 'url' && formData.hero_video && (
                    <div className="aspect-video bg-muted rounded-sm flex items-center justify-center">
                      <Video className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <div className="space-y-2">
                <Label htmlFor="photo_credits">Photo/Video Credits</Label>
                <Input
                  id="photo_credits"
                  name="photo_credits"
                  value={formData.photo_credits}
                  onChange={handleChange}
                  placeholder="Photo by..."
                  data-testid="photo-credits-input"
                />
              </div>
            </div>

            {/* Flags */}
            <div className="border border-border rounded-sm p-4 space-y-4">
              <h3 className="font-medium">Display Options</h3>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="is_headline_featured">Headline Story</Label>
                <Switch
                  id="is_headline_featured"
                  checked={formData.is_headline_featured}
                  onCheckedChange={async (checked) => {
                    setFormData(prev => ({ ...prev, is_headline_featured: checked }));
                    if (checked && isEditing) {
                      try {
                        await api.put(`/articles/${id}/set-headline`);
                        toast.success('Set as headline story');
                      } catch (err) {
                        toast.error('Failed to set headline');
                      }
                    }
                  }}
                  data-testid="is-headline-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_featured">Featured Article</Label>
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, is_featured: checked }))
                  }
                  data-testid="is-featured-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_breaking">Breaking News</Label>
                <Switch
                  id="is_breaking"
                  checked={formData.is_breaking}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, is_breaking: checked }))
                  }
                  data-testid="is-breaking-switch"
                />
              </div>
            </div>

            {/* SEO */}
            <div className="border border-border rounded-sm p-4 space-y-4">
              <h3 className="font-medium">SEO</h3>
              
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta Title</Label>
                <Input
                  id="meta_title"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  data-testid="meta-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  rows={2}
                  data-testid="meta-description-input"
                />
              </div>
            </div>

            {/* Editorial Notes - Only show when editing */}
            {isEditing && (
              <div className="border border-border rounded-sm p-4 space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Editorial Notes
                </h3>
                <p className="text-xs text-muted-foreground">
                  Internal notes visible to all staff members
                </p>
                
                {/* Add Note */}
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Add a note..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={2}
                    className="flex-1 text-sm"
                    data-testid="new-note-input"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="w-full"
                  data-testid="add-note-btn"
                >
                  {addingNote ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Add Note
                </Button>

                {/* Notes List */}
                {loadingNotes ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : notes.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {notes.map((note) => (
                      <div 
                        key={note.id} 
                        className="bg-secondary/30 p-3 rounded-sm space-y-2"
                        data-testid={`note-${note.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNote(note.id)}
                            className="shrink-0 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {note.author_name} • {formatDate(note.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    No notes yet
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Article Preview</span>
              <span className={`text-xs px-2 py-1 rounded-sm ${
                formData.status === 'published' ? 'bg-green-100 text-green-700' :
                formData.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                formData.status === 'in_review' ? 'bg-blue-100 text-blue-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                {formData.status.replace('_', ' ').toUpperCase()}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 space-y-6" data-testid="article-preview">
            {/* Category Badge */}
            {formData.category_id && (
              <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-wider bg-primary text-primary-foreground">
                {categories.find(c => c.id === formData.category_id)?.name || 'Category'}
              </span>
            )}

            {/* Title */}
            <h1 className="font-serif text-3xl md:text-4xl font-bold leading-tight">
              {formData.title || 'Untitled Article'}
            </h1>

            {/* Subheadline */}
            {formData.subheadline && (
              <p className="text-lg text-muted-foreground">
                {formData.subheadline}
              </p>
            )}

            {/* Meta */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground border-y border-border py-4">
              {formData.author && <span>By {formData.author}</span>}
              <span>{formatDate(formData.publish_date || new Date().toISOString())}</span>
            </div>

            {/* Hero Media */}
            {formData.hero_type === 'video' ? (
              formData.hero_video_type === 'upload' && formData.hero_video_file ? (
                <div className="aspect-video bg-black rounded-sm overflow-hidden">
                  <video
                    src={getImageUrl(formData.hero_video_file)}
                    className="w-full h-full object-cover"
                    controls
                  />
                </div>
              ) : formData.hero_video ? (
                <div className="aspect-video bg-black rounded-sm overflow-hidden">
                  <iframe
                    src={formData.hero_video.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')}
                    className="w-full h-full"
                    allowFullScreen
                    title="Video preview"
                  />
                </div>
              ) : null
            ) : formData.hero_image ? (
              <figure>
                <img
                  src={getImageUrl(formData.hero_image)}
                  alt={formData.title}
                  className="w-full max-h-[400px] object-cover object-top rounded-sm"
                />
                {formData.photo_credits && (
                  <figcaption className="text-center text-sm text-muted-foreground mt-2">
                    {formData.photo_credits}
                  </figcaption>
                )}
              </figure>
            ) : (
              <div className="aspect-video bg-secondary/50 rounded-sm flex items-center justify-center text-muted-foreground">
                No hero image set
              </div>
            )}

            {/* Body Content */}
            <div 
              className="prose prose-lg max-w-none article-body"
              dangerouslySetInnerHTML={{ __html: formData.body || '<p class="text-muted-foreground">No content yet...</p>' }}
            />

            {/* Gallery */}
            {formData.gallery_images?.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-serif text-xl font-bold">Photo Gallery</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.gallery_images.map((img, index) => (
                    <img
                      key={index}
                      src={getImageUrl(img)}
                      alt={`Gallery image ${index + 1}`}
                      className="w-full aspect-square object-cover rounded-sm"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Embedded Video */}
            {(formData.embedded_video || formData.embedded_video_file) && (
              <div className="space-y-4">
                <h3 className="font-serif text-xl font-bold">Video</h3>
                {formData.embedded_video_type === 'upload' && formData.embedded_video_file ? (
                  <div className="aspect-video bg-black rounded-sm overflow-hidden">
                    <video
                      src={getImageUrl(formData.embedded_video_file)}
                      className="w-full h-full object-cover"
                      controls
                    />
                  </div>
                ) : formData.embedded_video ? (
                  <div className="aspect-video bg-black rounded-sm overflow-hidden">
                    <iframe
                      src={formData.embedded_video.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title="Embedded video"
                    />
                  </div>
                ) : null}
              </div>
            )}

            {/* Tags */}
            {formData.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 text-sm bg-secondary rounded-sm">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ArticleEditor;
